import { type PropsWithChildren, useMemo, useState } from "react";

import { Button, Separator } from "@aircall/ds";
import { Plus } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";

import { NoteAgentDetail } from "@/graphql/generated/graphql";
import { useCreateNote } from "@/hooks/useCreateNote/useCreateNote";
import { SectionNotesForm } from "../SectionNotesForm";

import { NotesListProps } from "./NotesList.decl";
import { NotesListItem } from "./NotesListItem";

function byDateDesc(a: NoteAgentDetail, b: NoteAgentDetail) {
  const aDate = new Date(a.createdAt).getTime();
  const bDate = new Date(b.createdAt).getTime();

  return bDate - aDate;
}

function ListLayout({ children }: PropsWithChildren<unknown>) {
  return (
    <div data-test="notes-list" className="flex flex-col gap-4 w-full">
      {children}
    </div>
  );
}

export function NotesList({ notes }: NotesListProps) {
  const [displayButton, setDisplayButton] = useState(true);

  const { t } = useTranslation();
  const sorted = useMemo(() => [...notes].sort(byDateDesc), [notes]);

  const handleOnClick = () => {
    setDisplayButton(false);
  };
  const handleOnCancel = () => {
    setDisplayButton(true);
  };

  const { handleOnSubmit, loading: isLoadingMutation } = useCreateNote();

  const handleOnSubmitEnhanced = (e: React.FormEvent<HTMLFormElement>) => {
    handleOnSubmit(e);
    setDisplayButton(true);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <ListLayout>
        {sorted.map((note, index) => {
          if (index === 0) {
            return (
              <NotesListItem
                key={note.id}
                id={note.id}
                content={note.content}
                createdAt={note.createdAt}
                firstName={note.firstName}
                lastName={note.lastName}
                userId={note.userId}
                isLoadingMutation={isLoadingMutation}
              />
            );
          }

          return (
            <div key={note.id} className="flex flex-col gap-4">
              <Separator />
              <NotesListItem
                id={note.id}
                content={note.content}
                createdAt={note.createdAt}
                firstName={note.firstName}
                lastName={note.lastName}
                userId={note.userId}
                isLoadingMutation={isLoadingMutation}
              />
            </div>
          );
        })}
      </ListLayout>
      {displayButton && (
        <Button
          data-test="add_notes_button"
          onClick={handleOnClick}
          variant="ghost"
          size="lg"
          className="self-start"
        >
          <Plus className="size-4" />
          {t("add_note")}
        </Button>
      )}
      <SectionNotesForm
        show={!displayButton}
        onSubmit={handleOnSubmitEnhanced}
        onCancel={handleOnCancel}
        showSeparator
      />
    </div>
  );
}
